import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import api from "@/lib/api";

const QuoteContext = createContext(null);

export function QuoteProvider({ children }) {
  const [activeQuote, setActiveQuote] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("quotesys_active_quote") || "null");
    } catch {
      return null;
    }
  });
  const [count, setCount] = useState(0);
  const activeQuoteRef = useRef(activeQuote);

  const refreshCount = useCallback(async (quoteId) => {
    try {
      const { data } = await api.get(`/quotes/${quoteId}`);
      setCount(data.items.length);
    } catch (e) {
      if (e.response?.status === 404) {
        activeQuoteRef.current = null;
        localStorage.setItem("quotesys_active_quote", "null");
        setActiveQuote(null);
        setCount(0);
      }
    }
  }, []);

  useEffect(() => {
    if (activeQuote?.id) refreshCount(activeQuote.id);
    else setCount(0);
  }, [activeQuote, refreshCount]);

  const persistActive = useCallback((quote) => {
    const slim = quote ? { id: quote.id, quote_number: quote.quote_number, buyer: quote.buyer, customer_name: quote.customer_name } : null;
    // synchronous write so navigation right after selectQuote never races with setState
    activeQuoteRef.current = slim;
    localStorage.setItem("quotesys_active_quote", JSON.stringify(slim));
    setActiveQuote(slim);
    return slim;
  }, []);

  const selectQuote = useCallback((quote) => persistActive(quote), [persistActive]);

  const addToQuote = useCallback(async (itemNumber, quote) => {
    // Add an item to a quote. If `quote` given, it becomes the active quote first.
    // Returns the quote used, or null when there is no active quote (caller should open the picker).
    const target = quote ? persistActive(quote) : activeQuoteRef.current;
    if (!target) return null;
    const { data } = await api.post(`/quotes/${target.id}/items`, { item_number: itemNumber });
    setCount(data.items.length);
    return target;
  }, [persistActive]);

  const value = useMemo(
    () => ({ activeQuote, selectQuote, addToQuote, count, refreshCount }),
    [activeQuote, count, selectQuote, addToQuote, refreshCount]
  );

  return (
    <QuoteContext.Provider value={value}>
      {children}
    </QuoteContext.Provider>
  );
}

export const useQuote = () => useContext(QuoteContext);
